import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { CdkDrag, CdkDragEnd, CdkDragHandle } from '@angular/cdk/drag-drop';
import { ZagIcon } from '../icon';

/** Pixel offset the panel keeps from the viewport edge when clamping. */
const VIEWPORT_MARGIN = 8;
/** Minimum panel sliver that must stay visible when restoring a persisted position. */
const MIN_VISIBLE_PX = 56;

export interface FloatingPanelPosition {
  x: number;
  y: number;
}

/**
 * Floating Panel Component
 *
 * A draggable, collapsible floating panel anchored to the viewport. Generic
 * shell for tool windows that must never permanently cover the UI underneath:
 * the user can drag it anywhere (CDK drag), collapse it to a compact pill,
 * or reset its position via a keyboard-accessible header action.
 *
 * The component is stateless about persistence — consumers bind `position`
 * and `collapsed` models and store them wherever appropriate.
 *
 * @example
 * ```html
 * <zag-floating-panel
 *   panelTitle="Review"
 *   [(collapsed)]="collapsed"
 *   [(position)]="position"
 *   collapseAriaLabel="Collapse review panel"
 *   expandAriaLabel="Expand review panel"
 *   resetPositionAriaLabel="Reset panel position"
 * >
 *   <span zagFloatingPanelPill>3/7</span>
 *   <div>Panel body</div>
 * </zag-floating-panel>
 * ```
 */
@Component({
  selector: 'zag-floating-panel',
  imports: [CdkDrag, CdkDragHandle, ZagIcon],
  template: `
    <section
      class="zag-floating-panel__surface"
      cdkDrag
      cdkDragBoundary="body"
      [cdkDragFreeDragPosition]="dragPosition()"
      (cdkDragEnded)="onDragEnded($event)"
      role="complementary"
      [attr.aria-label]="panelTitle()"
    >
      @if (collapsed()) {
        <button type="button" class="zag-floating-panel__pill" [attr.aria-label]="expandAriaLabel()" (click)="setCollapsed(false)">
          <ng-content select="[zagFloatingPanelPill]" />
        </button>
      } @else {
        <div class="zag-floating-panel__card">
          <header class="zag-floating-panel__header" cdkDragHandle>
            <span class="zag-floating-panel__grip" zagIcon="drag-indicator" aria-hidden="true"></span>
            <h2 class="zag-floating-panel__title">{{ panelTitle() }}</h2>
            <span class="zag-floating-panel__actions">
              <button
                type="button"
                class="zag-floating-panel__action"
                [attr.aria-label]="resetPositionAriaLabel()"
                (click)="resetPosition()"
              >
                <span zagIcon="restart-alt" aria-hidden="true"></span>
              </button>
              <button type="button" class="zag-floating-panel__action" [attr.aria-label]="collapseAriaLabel()" (click)="setCollapsed(true)">
                <span zagIcon="remove" aria-hidden="true"></span>
              </button>
            </span>
          </header>
          <div class="zag-floating-panel__body">
            <ng-content />
          </div>
        </div>
      }
    </section>
  `,
  styleUrl: './floating-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-floating-panel]': 'true',
    '[class.zag-floating-panel--collapsed]': 'collapsed()',
    '(keydown.escape)': 'onEscape()',
  },
})
export class FloatingPanelComponent {
  /** Panel heading — consumers pass an already-translated string. */
  readonly panelTitle = input.required<string>();
  /** Collapsed-to-pill state; two-way bindable for persistence. */
  readonly collapsed = model<boolean>(false);
  /** Drag offset from the CSS anchor position; `null` = default anchor. */
  readonly position = model<FloatingPanelPosition | null>(null);

  /** Accessible labels — consumers pass already-translated strings. */
  readonly expandAriaLabel = input.required<string>();
  readonly collapseAriaLabel = input.required<string>();
  readonly resetPositionAriaLabel = input.required<string>();

  /**
   * CDK free-drag position derived from the persisted offset, clamped so a
   * position saved on a large viewport can never render the panel fully
   * off-screen when restored on a smaller one. The panel is anchored
   * bottom-right, so offsets beyond the anchor (positive) or beyond the
   * opposite edge (large negative) are pulled back into reach.
   */
  protected readonly dragPosition = computed<FloatingPanelPosition>(() => {
    const raw = this.position() ?? { x: 0, y: 0 };
    if (typeof window === 'undefined') return raw;
    return {
      x: Math.min(Math.max(raw.x, -(window.innerWidth - MIN_VISIBLE_PX)), 0),
      y: Math.min(Math.max(raw.y, -(window.innerHeight - MIN_VISIBLE_PX)), 0),
    };
  });

  protected setCollapsed(collapsed: boolean): void {
    this.collapsed.set(collapsed);
  }

  /** Keyboard fallback for pointer dragging: return to the default anchor. */
  protected resetPosition(): void {
    this.position.set(null);
  }

  /** ESC collapses the expanded panel so it never traps the tester. */
  protected onEscape(): void {
    if (!this.collapsed()) {
      this.collapsed.set(true);
    }
  }

  protected onDragEnded(event: CdkDragEnd): void {
    const raw = event.source.getFreeDragPosition();
    this.position.set(this.clampToViewport(event, raw));
  }

  /**
   * Keep the panel reachable after a drag: at least a margin-sized part of it
   * must remain inside the viewport (`cdkDragBoundary` handles the live drag,
   * this guards persisted positions restored on differently-sized viewports).
   */
  private clampToViewport(event: CdkDragEnd, raw: FloatingPanelPosition): FloatingPanelPosition {
    const element = event.source.element.nativeElement;
    const rect = element.getBoundingClientRect();
    const clampedX = raw.x - Math.max(0, rect.right - (window.innerWidth - VIEWPORT_MARGIN)) + Math.max(0, VIEWPORT_MARGIN - rect.left);
    const clampedY = raw.y - Math.max(0, rect.bottom - (window.innerHeight - VIEWPORT_MARGIN)) + Math.max(0, VIEWPORT_MARGIN - rect.top);
    return { x: clampedX, y: clampedY };
  }
}
